import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    FileText, Download, CheckCircle, Clock, XCircle, RefreshCw, Loader2, FilePlus, Shield, ShieldCheck,
} from "lucide-react";
import { getMyDealDocs, getBuyersOrderUrl, DealDocRecord } from "@/Calls/callDealJackets";
import {
    createInsuranceQuoteRequest, acceptInsuranceQuote, getMyInsuranceQuotesForDeal,
} from "@/Calls/callInsuranceAuth";
import {
    createWarrantyRequest, acceptWarrantyQuote, getMyWarrantyQuotesForDeal,
} from "@/Calls/callWarrantyAuth";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { API_URL } from "@/config";

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ElementType }> = {
    PENDING:   { label: "Pending Review",    color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",   icon: Clock },
    VIEWED:    { label: "Viewed by Dealer",  color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",       icon: RefreshCw },
    COUNTERED: { label: "Counter Offer",     color: "bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-400", icon: RefreshCw },
    ACCEPTED:  { label: "Accepted",          color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400", icon: CheckCircle },
    FUNDED:    { label: "Funded",            color: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400", icon: CheckCircle },
    DECLINED:  { label: "Declined",          color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",            icon: XCircle },
    CANCELLED: { label: "Cancelled",         color: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400",           icon: XCircle },
};

function fmt(v: string | null | undefined) {
    if (!v) return "—";
    const n = Number(v);
    if (isNaN(n)) return v;
    return `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function InsuranceSection({ doc, onUpdate }: { doc: DealDocRecord; onUpdate: () => void }) {
    const [quotes, setQuotes] = useState<any[]>([]);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [showQuotes, setShowQuotes] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [accepting, setAccepting] = useState<number | null>(null);

    const loadQuotes = async () => {
        setLoadingQuotes(true);
        try {
            const res = await getMyInsuranceQuotesForDeal(doc.deal_id);
            setQuotes(res.quotes ?? []);
            setShowQuotes(true);
        } finally {
            setLoadingQuotes(false);
        }
    };

    const handleConsent = async () => {
        setRequesting(true);
        try {
            await createInsuranceQuoteRequest({
                deal_id: doc.deal_id,
                vehicle_vin: doc.vVin ?? undefined,
                vehicle_year: doc.vYear ?? undefined,
                vehicle_make: doc.vMake ?? undefined,
                vehicle_model: doc.vModel ?? undefined,
                vehicle_type: "AUTO",
            });
            onUpdate();
        } catch (e: any) {
            alert(e.message ?? "Failed to submit request");
        } finally {
            setRequesting(false);
        }
    };

    const handleAccept = async (quoteId: number) => {
        setAccepting(quoteId);
        try {
            await acceptInsuranceQuote(quoteId);
            onUpdate();
        } catch (e: any) {
            alert(e.message ?? "Failed to accept quote");
        } finally {
            setAccepting(null);
        }
    };

    // Accepted insurance — show summary
    if (doc.insurance) {
        const ins = doc.insurance;
        const binderUrl = ins.binder_url
            ? ins.binder_url.startsWith("/")
                ? `${API_URL.replace("/api", "")}${ins.binder_url}`
                : ins.binder_url
            : null;
        return (
            <div className="pt-3 border-t">
                <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-semibold">Insurance</span>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">Active</Badge>
                </div>
                <div className="bg-muted rounded-lg p-3 text-sm space-y-1">
                    <p className="font-medium">{ins.company.company_name}</p>
                    {ins.coverage_type && <p className="text-muted-foreground">{ins.coverage_type}</p>}
                    {ins.premium_monthly && <p className="text-indigo-600 font-medium">${ins.premium_monthly}/mo</p>}
                    {ins.binder_number && <p className="text-muted-foreground">Binder: {ins.binder_number}</p>}
                    {binderUrl && (
                        <a href={binderUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline text-xs">
                            View Binder PDF
                        </a>
                    )}
                </div>
            </div>
        );
    }

    // Open quote request — show quote count + view quotes
    if (doc.insuranceRequest?.consent_given) {
        return (
            <div className="pt-3 border-t">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-blue-500" />
                        <span className="text-sm font-medium">Insurance Quotes</span>
                        {doc.insuranceRequest._count.quotes > 0 && (
                            <Badge variant="outline" className="text-xs">{doc.insuranceRequest._count.quotes} received</Badge>
                        )}
                    </div>
                    <Button
                        size="sm" variant="outline"
                        onClick={loadQuotes}
                        disabled={loadingQuotes}
                    >
                        {loadingQuotes ? <Loader2 className="h-3 w-3 animate-spin" /> : "View Quotes"}
                    </Button>
                </div>

                {showQuotes && quotes.length > 0 && (
                    <div className="mt-3 space-y-2">
                        {quotes.map((q: any) => (
                            <div key={q.quote_id} className="bg-muted rounded-lg p-3 flex items-start justify-between gap-3 text-sm">
                                <div>
                                    <p className="font-medium">{q.company?.company_name}</p>
                                    {q.coverage_type && <p className="text-muted-foreground">{q.coverage_type}</p>}
                                    {q.premium_monthly && <p className="text-indigo-600">${q.premium_monthly}/mo</p>}
                                    {q.deductible && <p className="text-muted-foreground">${q.deductible} deductible</p>}
                                </div>
                                {q.status === "PENDING" && (
                                    <Button
                                        size="sm"
                                        className="bg-blue-600 text-white hover:bg-blue-700 shrink-0"
                                        onClick={() => handleAccept(q.quote_id)}
                                        disabled={accepting === q.quote_id}
                                    >
                                        {accepting === q.quote_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Accept"}
                                    </Button>
                                )}
                                {q.status === "ACCEPTED" && <Badge className="bg-green-100 text-green-700 hover:bg-green-100 shrink-0">Accepted</Badge>}
                            </div>
                        ))}
                    </div>
                )}
                {showQuotes && quotes.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-2">No quotes received yet. Insurance companies will submit quotes shortly.</p>
                )}
            </div>
        );
    }

    // No request yet — show consent toggle
    return (
        <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Insurance Marketplace</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
                Allow IDSCRM to share your vehicle and contact information with licensed insurance carriers. They will compete for your business with quotes — no obligation. You select or decline.
            </p>
            <Button
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={handleConsent}
                disabled={requesting}
            >
                {requesting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Shield className="h-3 w-3 mr-1" />}
                Get Insurance Quotes
            </Button>
        </div>
    );
}

function WarrantySection({ doc, onUpdate }: { doc: DealDocRecord; onUpdate: () => void }) {
    const [quotes, setQuotes] = useState<any[]>([]);
    const [loadingQuotes, setLoadingQuotes] = useState(false);
    const [showQuotes, setShowQuotes] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [accepting, setAccepting] = useState<number | null>(null);

    const loadQuotes = async () => {
        setLoadingQuotes(true);
        try {
            const res = await getMyWarrantyQuotesForDeal(doc.deal_id);
            setQuotes(res.data.quotes ?? []);
            setShowQuotes(true);
        } finally {
            setLoadingQuotes(false);
        }
    };

    const handleConsent = async () => {
        setRequesting(true);
        try {
            await createWarrantyRequest({
                deal_id: doc.deal_id,
                vehicle_vin: doc.vVin ?? undefined,
                vehicle_year: doc.vYear ?? undefined,
                vehicle_make: doc.vMake ?? undefined,
                vehicle_model: doc.vModel ?? undefined,
                vehicle_type: "AUTO",
            });
            onUpdate();
        } catch (e: any) {
            alert(e.message ?? "Failed to submit request");
        } finally {
            setRequesting(false);
        }
    };

    const handleAccept = async (quoteId: number) => {
        setAccepting(quoteId);
        try {
            await acceptWarrantyQuote(quoteId);
            onUpdate();
        } catch (e: any) {
            alert(e.message ?? "Failed to accept quote");
        } finally {
            setAccepting(null);
        }
    };

    // Accepted warranty — show summary
    if (doc.warranty) {
        const w = doc.warranty;
        const contractUrl = w.contract_url
            ? w.contract_url.startsWith("/")
                ? `${API_URL.replace("/api", "")}${w.contract_url}`
                : w.contract_url
            : null;
        return (
            <div className="pt-3 border-t">
                <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="h-4 w-4 text-purple-600" />
                    <span className="text-sm font-semibold">Warranty</span>
                    <Badge className="bg-green-100 text-green-700 hover:bg-green-100 text-xs">Active</Badge>
                </div>
                <div className="bg-muted rounded-lg p-3 text-sm space-y-1">
                    <p className="font-medium">{w.company.company_name}</p>
                    {w.coverage_type && <p className="text-muted-foreground">{w.coverage_type.replace("_", " ")}</p>}
                    {w.coverage_months && <p className="text-muted-foreground">{w.coverage_months} months / {w.coverage_miles ?? "unlimited"} miles</p>}
                    {w.monthly_cost && <p className="text-purple-600 font-medium">${w.monthly_cost}/mo</p>}
                    {w.contract_number && <p className="text-muted-foreground">Contract: {w.contract_number}</p>}
                    {contractUrl && (
                        <a href={contractUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 underline text-xs">
                            View Contract PDF
                        </a>
                    )}
                </div>
            </div>
        );
    }

    // Open warranty request — show quote count + view
    if (doc.warrantyRequest?.consent_given) {
        return (
            <div className="pt-3 border-t">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-purple-500" />
                        <span className="text-sm font-medium">Warranty Quotes</span>
                        {doc.warrantyRequest._count.quotes > 0 && (
                            <Badge variant="outline" className="text-xs">{doc.warrantyRequest._count.quotes} received</Badge>
                        )}
                    </div>
                    <Button size="sm" variant="outline" onClick={loadQuotes} disabled={loadingQuotes}>
                        {loadingQuotes ? <Loader2 className="h-3 w-3 animate-spin" /> : "View Quotes"}
                    </Button>
                </div>

                {showQuotes && quotes.length > 0 && (
                    <div className="mt-3 space-y-2">
                        {quotes.map((q: any) => (
                            <div key={q.quote_id} className="bg-muted rounded-lg p-3 flex items-start justify-between gap-3 text-sm">
                                <div>
                                    <p className="font-medium">{q.company?.company_name}</p>
                                    {q.coverage_type && <p className="text-muted-foreground">{q.coverage_type.replace("_", " ")}</p>}
                                    {q.coverage_months && <p className="text-muted-foreground">{q.coverage_months}mo / {q.coverage_miles ?? "unlim"}</p>}
                                    {q.monthly_cost && <p className="text-purple-600">${q.monthly_cost}/mo</p>}
                                    {q.deductible && <p className="text-muted-foreground">${q.deductible} deductible</p>}
                                </div>
                                {q.status === "PENDING" && (
                                    <Button
                                        size="sm"
                                        className="bg-purple-600 text-white hover:bg-purple-700 shrink-0"
                                        onClick={() => handleAccept(q.quote_id)}
                                        disabled={accepting === q.quote_id}
                                    >
                                        {accepting === q.quote_id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Accept"}
                                    </Button>
                                )}
                                {q.status === "ACCEPTED" && <Badge className="bg-green-100 text-green-700 hover:bg-green-100 shrink-0">Accepted</Badge>}
                            </div>
                        ))}
                    </div>
                )}
                {showQuotes && quotes.length === 0 && (
                    <p className="text-sm text-muted-foreground mt-2">No quotes received yet. Warranty companies will submit quotes shortly.</p>
                )}
            </div>
        );
    }

    // No request yet — show consent toggle
    return (
        <div className="pt-3 border-t">
            <div className="flex items-center gap-2 mb-2">
                <ShieldCheck className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Warranty Marketplace</span>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
                Allow IDSCRM to share your vehicle details with licensed warranty providers. They will compete with coverage quotes — no obligation. You select or decline.
            </p>
            <Button
                size="sm"
                variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={handleConsent}
                disabled={requesting}
            >
                {requesting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ShieldCheck className="h-3 w-3 mr-1" />}
                Get Warranty Quotes
            </Button>
        </div>
    );
}

function DealDocCard({ doc, onUpdate }: { doc: DealDocRecord; onUpdate: () => void }) {
    const status = doc.deal_status ?? "PENDING";
    const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG.PENDING;
    const Icon = cfg.icon;
    const isFinance = (doc.deal_type ?? "FINANCE") !== "CASH";
    const hasCreditApp = !!doc.creditApp;
    const pdfUrl = getBuyersOrderUrl(doc.deal_id);

    return (
        <Card className="p-5 space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <p className="font-medium">
                        {doc.vYear} {doc.vMake} {doc.vModel}
                    </p>
                    {doc.vVin && (
                        <p className="text-xs text-muted-foreground font-mono mt-0.5">VIN: {doc.vVin}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-0.5">
                        Deal #{doc.deal_number || doc.deal_id} · {new Date(doc.deal_created_at).toLocaleDateString()}
                    </p>
                </div>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${cfg.color}`}>
                    <Icon className="h-3 w-3" />
                    {cfg.label}
                </span>
            </div>

            {/* Financial summary */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Vehicle Price</p>
                    <p className="font-medium">{fmt(doc.priceVehicle)}</p>
                </div>
                {isFinance ? (
                    <>
                        <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Monthly Payment</p>
                            <p className="font-medium text-indigo-600 dark:text-indigo-400">
                                {doc.monthlypymts ? `${fmt(doc.monthlypymts)}/mo` : "—"}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-muted-foreground uppercase tracking-wide">Term / APR</p>
                            <p className="font-medium">
                                {doc.term ? `${doc.term} mo` : "—"}{doc.apr ? ` · ${doc.apr}%` : ""}
                            </p>
                        </div>
                    </>
                ) : (
                    <div>
                        <p className="text-xs text-muted-foreground uppercase tracking-wide">Cash Due</p>
                        <p className="font-medium text-indigo-600 dark:text-indigo-400">{fmt(doc.amountDDue)}</p>
                    </div>
                )}
            </div>

            {/* Document actions */}
            <div className="flex flex-wrap items-center gap-2 pt-1 border-t">
                {doc.deal_locked ? (
                    <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            Buyer's Order PDF
                        </Button>
                    </a>
                ) : (
                    <Button size="sm" variant="outline" disabled title="Available once deal is finalized">
                        <FileText className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                        Buyer's Order (Pending)
                    </Button>
                )}

                {hasCreditApp ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                        <CheckCircle className="h-3 w-3" />
                        Credit App Submitted
                    </span>
                ) : isFinance ? (
                    <Link to={`/customer/credit-app?dealId=${doc.deal_id}`}>
                        <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
                            <FilePlus className="h-3.5 w-3.5 mr-1.5" />
                            Apply for Financing
                        </Button>
                    </Link>
                ) : null}
            </div>

            {/* Insurance section */}
            <InsuranceSection doc={doc} onUpdate={onUpdate} />

            {/* Warranty section */}
            <WarrantySection doc={doc} onUpdate={onUpdate} />
        </Card>
    );
}

export default function CustomerDocumentsPage() {
    const [docs, setDocs] = useState<DealDocRecord[]>([]);
    const [loading, setLoading] = useState(true);

    const loadDocs = () => {
        setLoading(true);
        getMyDealDocs()
            .then(setDocs)
            .catch(console.error)
            .finally(() => setLoading(false));
    };

    useEffect(loadDocs, []);

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">My Documents</h1>
                <p className="text-muted-foreground text-sm mt-1">
                    Download your Buyer's Orders, manage credit applications, and get insurance and warranty quotes.
                </p>
            </div>

            {docs.length === 0 ? (
                <div className="text-center py-20 space-y-4">
                    <FileText className="h-12 w-12 mx-auto text-muted-foreground/30" />
                    <p className="text-muted-foreground">No deal documents yet.</p>
                    <Link to="/customer/marketplace" className="inline-block text-sm text-indigo-600 hover:underline">
                        Browse vehicles to start a deal
                    </Link>
                </div>
            ) : (
                <div className="space-y-4">
                    {docs.map((doc) => <DealDocCard key={doc.deal_id} doc={doc} onUpdate={loadDocs} />)}
                </div>
            )}
        </div>
    );
}
